/**
 * <ym-preview-lightbox>: shows the design preview of a personalized cart line
 * (snippets/ym-preview-lightbox.liquid).
 *
 * - Any link with [data-ym-preview-open] opens its href as an image here instead of following it:
 *   the apps' preview files are served as downloads, so following the link saves a file. The
 *   click is taken on window in the capture phase: Easify's cart script rewrites the preview link
 *   and handles its clicks too.
 * - Closes on Close, the X, a click on the backdrop and Esc; focus goes back to the link.
 * - A preview that does not load (an app can write it a few seconds after the add) shows a short
 *   message instead of a broken image.
 */
class YmPreviewLightbox extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.onClick);
    window.addEventListener('click', this.onOpenerClick, true);
    this.img?.addEventListener('error', () => this.showError(true));
    this.img?.addEventListener('load', () => this.showError(false));
    this.dialog?.addEventListener('close', () => {
      this.opener?.focus?.({ preventScroll: true });
      this.opener = null;
    });
  }

  disconnectedCallback() {
    window.removeEventListener('click', this.onOpenerClick, true);
  }

  get dialog() {
    return /** @type {HTMLDialogElement | null} */ (this.querySelector('[data-ym-lightbox]'));
  }

  get img() {
    return /** @type {HTMLImageElement | null} */ (this.querySelector('[data-ym-lightbox-img]'));
  }

  /** @param {MouseEvent} event */
  onOpenerClick = (event) => {
    const link = /** @type {HTMLElement} */ (event.target).closest?.('a[data-ym-preview-open]');
    if (!(link instanceof HTMLAnchorElement) || !link.href) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    this.open(link.href, link);
  };

  /** @param {MouseEvent} event */
  onClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    if (target === this.dialog || target.closest('[data-ym-lightbox-close]')) this.dialog?.close();
  };

  /** @param {string} src @param {HTMLElement} opener */
  open(src, opener) {
    const dialog = this.dialog;
    const img = this.img;
    if (!dialog || !img) return;
    this.opener = opener;
    this.showError(false);
    img.src = src;
    if (!dialog.open) dialog.showModal();
    /** @type {HTMLElement | null} */ (dialog.querySelector('.ym-lightbox__close'))?.focus();
  }

  /** @param {boolean} show */
  showError(show) {
    const img = this.img;
    const error = /** @type {HTMLElement | null} */ (this.querySelector('[data-ym-lightbox-error]'));
    if (img) img.hidden = show;
    if (error) error.hidden = !show;
  }
}

if (!customElements.get('ym-preview-lightbox')) customElements.define('ym-preview-lightbox', YmPreviewLightbox);
